/* eslint-disable @typescript-eslint/no-unsafe-assignment */
// @ts-nocheck — slated for deletion
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { snakeCaseToCamelCase } from "@/utils/formatters"
import { DateTime } from "luxon"
import type { Interval } from "luxon"
import type { CollectionProxy } from "spraypaint/lib-esm/proxies"

import ReportingData from "../../../../models/reportingData"
import type {
  GresbData,
  GresbDataDto,
  GresbEnergy,
  GresbEnergyDto,
  GresbGhg,
  GresbGhgDto,
  GresbWater,
  GresbWaterDto,
} from "../../models/gresb"

/**
 * Converts GRESB energy DTOs into GRESB energy objects
 *
 * @returns The GRESB energy objects
 * @example
 * getGresbEnergyFromDtos(gresbEnergyDtos)
 */
export const getGresbEnergyFromDtos = (
  energyDtos: GresbEnergyDto[]
): GresbEnergy[] =>
  energyDtos.map((dto) => ({
    ...dto,
    electricityConsumptionKwh: Number.parseFloat(dto.electricityConsumptionKwh),
    fuelConsumptionKwh: Number.parseFloat(dto.fuelConsumptionKwh),
    year: DateTime.fromISO(dto.startDate).year,
  }))

/**
 * Converts GRESB GHG DTOs into GRESB GHG objects
 *
 * @returns The GRESB GHG objects
 * @example
 * getGresbGhgFromDtos(gresbGhgDtos)
 */
export const getGresbGhgFromDtos = (ghgDtos: GresbGhgDto[]): GresbGhg[] =>
  ghgDtos.map((dto) => ({
    ...dto,
    scope1MtCo2E: Number.parseFloat(dto.scope1MtCo2E),
    scope2AdvancedGridStudyMtCo2E: Number.parseFloat(
      dto.scope2AdvancedGridStudyMtCo2E
    ),
    scope2LocationBasedMtCo2E: Number.parseFloat(dto.scope2LocationBasedMtCo2E),
    scope2MarketBasedMtCo2E: Number.parseFloat(dto.scope2MarketBasedMtCo2E),
    scope3MtCo2E: Number.parseFloat(dto.scope3MtCo2E),
    year: DateTime.fromISO(dto.startDate).year,
  }))

/**
 * Converts GRESB water DTOs into GRESB water objects
 *
 * @returns The GRESB water objects
 * @example
 * getGresbWaterFromDtos(gresbWaterDtos)
 */
export const getGresbWaterFromDtos = (
  waterDtos: GresbWaterDto[]
): GresbWater[] =>
  waterDtos.map((dto) => ({
    ...dto,
    waterConsumptionM3: Number.parseFloat(dto.waterConsumptionM3),
    year: DateTime.fromISO(dto.startDate).year,
  }))

/**
 * Normalizes a response's keys into snake-case from ReportingData
 * and converts its values to Energy, Ghg, and Water objects
 */
export const processGresbResponse = (
  value: CollectionProxy<ReportingData>
): GresbData => {
  const reportDataDto: GresbDataDto = snakeCaseToCamelCase(
    value.meta.stats.gresb_service.calculate
  )

  const reportData: GresbData = {
    energy: getGresbEnergyFromDtos(reportDataDto.energy),
    ghg: getGresbGhgFromDtos(reportDataDto.ghg),
    water: getGresbWaterFromDtos(reportDataDto.water),
  }

  return reportData
}

/**
 * Fetches GRESB report data for the given organization and timeframe
 *
 * @param orgId - The organization id
 * @param dateRange - The date range for the report data
 * @returns The report data
 * @example
 * fetchGresbReport(
 *   "1",
 *   Interval.fromISO("2022-01-01/2022-02-28")
 * )
 */
export const fetchGresbReport = async (
  orgId: string,
  dateRange: Interval
): Promise<GresbData> =>
  ReportingData.where({
    organization_id: Number.parseInt(orgId, 10),
    year: dateRange.start?.year,
    start_month: dateRange.start?.month,
    end_month: dateRange.end?.month,
  })
    .stats({
      gresb_service: "calculate",
    })
    .all()
    .then(processGresbResponse)

export default {
  getGresbEnergyFromDtos,
  getGresbGhgFromDtos,
  getGresbWaterFromDtos,
  fetchGresbReport,
}
